import type {
  AnalysisConfigSummary,
  AnalysisGroup,
  ExecutionConfigRef,
  UpdateGroupDraft,
} from '../model.js';
import { formatPct } from './product-metric.js';

/**
 * What a group is, and what its latest execution ran with (product owner, 2026-09-26).
 *
 * A group is a set of videos: a name, what it is for, and its members. It holds no configuration,
 * threshold nor engines — each execution chooses them at launch (`lib/launch-execution.ts`) and
 * records them. So wherever a screen used to say «the group's configuration», it now says what the
 * group's latest execution ran with (`AnalysisGroup.latestParameters`), or «Sin ejecuciones» when
 * there is none. Editing a group is its name and its description, and nothing waits for an execution
 * to end: nothing it measures lives on it.
 */

type Latest = Pick<AnalysisGroup, 'latestParameters'>;
type Named = Pick<AnalysisConfigSummary, 'id' | 'name'>;

/** What the screens say when a group has no execution to read parameters from. */
export const NO_EXECUTIONS = 'Sin ejecuciones';

/** The configuration the latest execution ran with, or null when it never ran or it is unknown. */
export function latestConfigRef(group: Latest): ExecutionConfigRef | null {
  return group.latestParameters?.config ?? null;
}

/** The id of the latest execution's configuration, or null. */
export function latestConfigId(group: Latest): number | null {
  return latestConfigRef(group)?.id ?? null;
}

/**
 * A configuration's name: the one the execution recorded, else today's from the list, else
 * «Configuración #id». Null when there is no configuration at all.
 */
export function configNameOf(
  ref: Pick<ExecutionConfigRef, 'id' | 'name'> | null | undefined,
  configs: readonly Named[] | null | undefined,
): string | null {
  if (!ref) return null;
  if (ref.name) return ref.name;
  if (ref.id === null) return null;

  return configs?.find((config) => config.id === ref.id)?.name ?? `Configuración #${ref.id}`;
}

/**
 * «Gestos base · v5»: the latest execution's configuration. «Sin ejecuciones» when the group never
 * ran, «Configuración desconocida» when it ran and nothing says with what.
 */
export function latestConfigLabel(
  group: Latest,
  configs: readonly Named[] | null | undefined,
): string {
  if (!group.latestParameters) return NO_EXECUTIONS;

  const ref = latestConfigRef(group);
  const name = configNameOf(ref, configs);
  if (name === null) return 'Configuración desconocida';

  return ref?.version != null ? `${name} · v${ref.version}` : name;
}

/** The latest execution's configuration name without its version: a «Por configuración» family. */
export function latestFamilyLabel(
  group: Latest,
  configs: readonly Named[] | null | undefined,
): string {
  if (!group.latestParameters) return NO_EXECUTIONS;

  return configNameOf(latestConfigRef(group), configs) ?? 'Configuración desconocida';
}

/**
 * «60 %», «De la configuración» for none chosen, «—» when unknown — the threshold of an execution
 * older than the launch records was never written down.
 */
export function thresholdLabel(thresholdPct: number | null | undefined, known = true): string {
  if (typeof thresholdPct === 'number') return formatPct(thresholdPct);

  return known ? 'De la configuración' : '—';
}

/**
 * «Gestos base · v5 · umbral 60 %»: what the latest execution ran with, in one line, for the launch
 * dialog to show beside a choice that opens on the active configuration instead. Null when the
 * group never ran. `configThresholdPct` says «the configuration's own» in numbers when known
 * (product owner, 2026-09-28: «quiero siempre ver el %»).
 */
export function latestParametersLine(
  group: Latest,
  configs: readonly Named[] | null | undefined,
  configThresholdPct: number | null = null,
): string | null {
  const latest = group.latestParameters;
  if (!latest) return null;

  const own = latest.thresholdPct ?? configThresholdPct;
  const threshold = own === null ? 'umbral de la configuración' : `umbral ${formatPct(own)}`;

  return `${latestConfigLabel(group, configs)} · ${threshold}`;
}

/**
 * A lookup of configuration names for the reasons of «Desactualizado», which name the configuration
 * by id only: the latest execution's own record first, then the list.
 */
export function configNameResolver(
  group: Latest,
  configs: readonly Named[] | null | undefined,
): (configId: number | null) => string | null {
  const latest = latestConfigRef(group);

  return (configId) =>
    configNameOf(latest && latest.id === configId ? latest : { id: configId, name: null }, configs);
}

// ── Editing the group ────────────────────────────────────────────────────────

export interface GroupEditForm {
  name: string;
  description: string;
}

export function groupEditForm(group: Pick<AnalysisGroup, 'name' | 'description'>): GroupEditForm {
  return { name: group.name, description: group.description ?? '' };
}

/**
 * What to send, or why it cannot be sent. `draft: null` with no error is «nothing changed».
 *
 * Only what changed travels. An emptied description is a decision — it is sent as null — and
 * spaces around either field are not a change.
 */
export function groupEditChange(
  group: Pick<AnalysisGroup, 'name' | 'description'>,
  form: GroupEditForm,
): { draft: UpdateGroupDraft | null; error: string | null } {
  const name = form.name.trim();
  if (name === '') return { draft: null, error: 'El grupo necesita un nombre' };

  const description = form.description.trim() || null;
  const draft: UpdateGroupDraft = {};

  if (name !== group.name) draft.name = name;
  if (description !== (group.description?.trim() || null)) draft.description = description;

  return { draft: Object.keys(draft).length > 0 ? draft : null, error: null };
}
